import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import buyingPostAuditQueueApi from '../../api/buyingPostAuditQueue';
import moment from 'moment';

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 分页查询审核队列
export const fetchBuyingPostAuditQueueList = createAsyncThunk(
  'buyingPostAuditQueue/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        title,
        auditStatus,
        auditType,
        startTime,
        endTime,
        ...otherParams
      } = params;

      const queryParams = {
        page,
        pageSize,
        title,
        auditStatus,
        auditType,
        startTime,
        endTime,
        ...otherParams
      };

      const response = await buyingPostAuditQueueApi.pageAuditQueues(queryParams);
      
      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(item => ({
          ...item,
          createTime: formatTimeArray(item.createTime),
          updateTime: formatTimeArray(item.updateTime),
          auditTime: item.auditTime ? formatTimeArray(item.auditTime) : null,
          
          // 审核状态映射
          auditStatusText: {
            0: '审核中',
            1: '审核通过',
            2: '审核驳回',
            3: '重新提交'
          }[item.auditStatus] || '未知状态',

          // 审核类型映射
          auditTypeText: {
            1: '新增',
            2: '编辑'
          }[item.auditType] || '未知类型',

          // 价格范围处理
          priceRange: item.expectedMinPrice && item.expectedMaxPrice 
            ? `¥${item.expectedMinPrice} - ¥${item.expectedMaxPrice}` 
            : '未指定价格范围',

          // 图片处理
          imageList: item.images || []
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取审核队列列表失败' });
    }
  }
);

// 获取审核队列详情
export const fetchBuyingPostAuditQueueDetail = createAsyncThunk(
  'buyingPostAuditQueue/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await buyingPostAuditQueueApi.getAuditQueueDetail(id);
      
      // 处理详情数据
      const detail = response.data;
      
      return {
        ...detail,
        createTime: formatTimeArray(detail.createTime),
        updateTime: formatTimeArray(detail.updateTime),
        auditTime: detail.auditTime ? formatTimeArray(detail.auditTime) : null,
        
        // 审核状态映射
        auditStatusText: {
          0: '待审核',
          1: '审核通过',
          2: '审核驳回'
        }[detail.auditStatus] || '未知状态',

        // 审核类型映射
        auditTypeText: {
          1: '新增',
          2: '编辑'
        }[detail.auditType] || '未知类型',

        // 价格范围处理
        priceRange: detail.expectedMinPrice && detail.expectedMaxPrice 
          ? `¥${detail.expectedMinPrice} - ¥${detail.expectedMaxPrice}` 
          : '未指定价格范围',

        // 图片处理
        imageList: detail.images || []
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取审核队列详情失败' });
    }
  }
);

// 审核求购帖
export const auditBuyingPostQueue = createAsyncThunk(
  'buyingPostAuditQueue/audit',
  async ({ id, status, reason }, { rejectWithValue, dispatch }) => {
    try {
      const response = await buyingPostAuditQueueApi.auditBuyingPostQueue(id, { 
        status, 
        reason 
      });
      
      // 刷新列表
      dispatch(fetchBuyingPostAuditQueueList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '审核求购帖失败' });
    }
  }
);

// 删除审核队列
export const deleteBuyingPostAuditQueue = createAsyncThunk(
  'buyingPostAuditQueue/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await buyingPostAuditQueueApi.deleteAuditQueue(id);
      
      // 刷新列表
      dispatch(fetchBuyingPostAuditQueueList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '删除审核队列失败' });
    }
  }
);

const buyingPostAuditQueueSlice = createSlice({
  name: 'buyingPostAuditQueue',
  initialState: {
    auditQueues: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentAuditQueue: null,
    auditQueueDetail: null,
    detailLoading: false,
    detailError: null
  },
  reducers: {
    setCurrentAuditQueue: (state, action) => {
      state.currentAuditQueue = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取审核队列列表
    builder.addCase(fetchBuyingPostAuditQueueList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBuyingPostAuditQueueList.fulfilled, (state, action) => {
      state.loading = false;
      state.auditQueues = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchBuyingPostAuditQueueList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取审核队列详情
    builder.addCase(fetchBuyingPostAuditQueueDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(fetchBuyingPostAuditQueueDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.auditQueueDetail = action.payload;
    });
    builder.addCase(fetchBuyingPostAuditQueueDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.payload;
    });
  }
});

export const { setCurrentAuditQueue } = buyingPostAuditQueueSlice.actions;
export default buyingPostAuditQueueSlice.reducer;
